/**
 * NẠP ẢNH AI VÀO HỆ XƯƠNG.
 *
 * Quy trình: ảnh "bộ phận" do agy vẽ (lưới 3×3, nền magenta)
 *   → tách nền magenta (chroma key)
 *   → cắt 9 ô theo lưới
 *   → tự cắt sát viền (trim) để biết ĐIỂM KHỚP nằm ở đâu
 *   → nạp vào atlas chung
 *   → dựng xương theo đúng kích thước ảnh thật
 *
 * Nhờ vậy: ảnh AI quyết định tỉ lệ nhân vật, không phải hằng số trong code.
 */
import Phaser from 'phaser';
import type { PartFactory } from './parts';
import { MAX_AI_FRAME, PARTS_ATLAS } from './parts';
import { artPartsUrl, artUrl } from './art-url';
import type { ClassId } from '../data/classes';

export interface AiPart {
  key: string;
  w: number;
  h: number;
  /**
   * ĐIỂM KHỚP THẬT, toạ độ so với TÂM hộp bao (đơn vị pixel gốc).
   * Mảnh tay gập có vai nằm lệch về một bên, không phải giữa mép trên hộp bao.
   * Dùng điểm này làm gốc xoay thì khớp mới kín khi tay/chân vung mạnh.
   */
  topX: number;
  topY: number;
  /** điểm cuối mảnh (cổ tay / mắt cá) — để gắn bàn tay, ủng cho khớp */
  botX: number;
  botY: number;
  /** màu trung bình các pixel đặc — dùng cho nắp khớp */
  color: number;
}

/**
 * XỬ LÝ 1 Ô ẢNH TRONG **MỘT LƯỢT** — tách nền, lọc khối, cắt sát viền, dò điểm khớp,
 * lấy màu trung bình, thu nhỏ cho vừa atlas.
 *
 * ⚠️ VÌ SAO GỘP: bản đầu tiên gọi `getImageData`/`putImageData` 5 lần cho mỗi ô
 * (chroma → blob → trim → analyze) và boot mất ~13 giây (đo bằng Chrome thật).
 * Gộp lại còn 1 lần đọc + 1 lần ghi + quét điểm ảnh ĐÚNG MỘT VÒNG.
 *
 * Trả về `null` nếu ô trống.
 */
function processCell(
  cell: HTMLCanvasElement,
  maxDim = MAX_AI_FRAME,
): { canvas: HTMLCanvasElement; w: number; h: number; topX: number; topY: number; botX: number; botY: number; color: number } | null {
  const w = cell.width;
  const h = cell.height;
  const ctx = cell.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;

  // 1) tách nền magenta  2) giữ khối liền lớn nhất (mảnh hay tràn sang ô bên cạnh)
  chromaKeyMagenta(d);
  keepMainBlob(d, w, h);

  // 3) MỘT vòng quét: hộp bao + dải trên/ dưới + màu trung bình
  /*
   * QUÉT 2 LƯỢT — và lượt 1 PHẢI xong mới biết dải trên/ dưới nằm ở đâu.
   *
   * ⚠️ BẪY NẶNG ĐÃ SẬP (triệu chứng: "bàn chân hơi hở và lệch một chút xíu"):
   * Bản cũ tính dải 9% theo **CẢ Ô ẢNH** (`y < 0.09·cellH`). Nhưng ô 232px còn mảnh
   * vẽ bên trong nhỏ hơn nhiều (ủng chỉ cao 123px và nằm giữa ô) ⇒ dải "trên" của ô
   * rơi vào vùng TRỐNG, chỉ còn vài pixel lẻ ⇒ điểm khớp ra rác:
   *   ủng: `topX` lưu là **0,5** trong khi đỉnh ủng thật ở **−26,9** ⇒ ủng lệch ngang
   *   ~3,9px và hở ~2,4px so với mắt cá (đo được trên game thật).
   * Cách đúng: dải 9% tính theo **CHÍNH MẢNH** (hộp bao của mảnh), không theo ô.
   */
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  let cN = 0;
  let cr = 0;
  let cg = 0;
  let cb = 0;
  // LƯỢT 1: hộp bao + màu trung bình
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (d[i + 3] <= 16) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      cN++;
      cr += d[i];
      cg += d[i + 1];
      cb += d[i + 2];
    }
  }
  if (maxX < 0) return null;

  // LƯỢT 2: điểm khớp, dải tính theo chiều cao THẬT của mảnh
  const contentH = maxY - minY + 1;
  const band = Math.max(2, Math.round(contentH * 0.09));
  let topN = 0;
  let topSx = 0;
  let topSy = 0;
  let botN = 0;
  let botSx = 0;
  let botSy = 0;
  for (let y = minY; y <= maxY; y++) {
    const inTop = y < minY + band;
    const inBot = y > maxY - band;
    for (let x = minX; x <= maxX; x++) {
      const i = (y * w + x) * 4;
      if (d[i + 3] <= 16) continue;
      if (inTop) {
        topN++;
        topSx += x;
        topSy += y;
      }
      if (inBot) {
        botN++;
        botSx += x;
        botSy += y;
      }
    }
  }

  // 4) cắt sát viền (chừa 2px lề để viền đen không bị cụt)
  const pad = 2;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad);
  maxY = Math.min(h - 1, maxY + pad);
  const tw = maxX - minX + 1;
  const th = maxY - minY + 1;

  // 5) cắt bằng putImageData với offset âm (1 lệnh, không cần canvas tạm thứ hai)
  const crop = document.createElement('canvas');
  crop.width = tw;
  crop.height = th;
  crop.getContext('2d')?.putImageData(img, -minX, -minY);

  // 6) thu nhỏ cho vừa atlas
  const s = Math.min(1, maxDim / Math.max(tw, th));
  let out = crop;
  if (s < 1) {
    out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(tw * s));
    out.height = Math.max(1, Math.round(th * s));
    const octx = out.getContext('2d');
    if (octx) {
      octx.imageSmoothingEnabled = true;
      octx.imageSmoothingQuality = 'high';
      octx.drawImage(crop, 0, 0, out.width, out.height);
    }
  }
  const toLocal = (px: number, py: number): [number, number] => [
    (px - minX + 0.5) * s - out.width / 2,
    (py - minY + 0.5) * s - out.height / 2,
  ];
  const [topX, topY] = toLocal(topN ? topSx / topN : tw / 2 + minX, topN ? topSy / topN : minY);
  /*
   * ⚠️⚠️ `botX` KHÔNG lấy trung bình pixel đáy — nó rất nhạy với ảnh đầu QUAY NGHIÊNG.
   *
   * Người dùng: *"army đầu bị trượt khỏi thân (lệch ra sau lưng)"*.
   * NGUYÊN NHÂN ĐO ĐƯỢC: `botX` cũ = trung bình x của các pixel ở 10 hàng đáy, tức chỗ CỔ. Đầu của
   * `army` vẽ nghiêng (nhìn sang phải) nên cổ lệch hẳn một bên: đo `botX - tâm hộp bao` =
   *   strength +102 · agility +103 · street +106 · **army +113** · mother +102
   * ⇒ `army` lệch **6,5px** so với 4 class kia. `character.ts` canh đầu bằng `ox = -botX` nên sai
   * số đó thành lệch thật trên sân.
   *
   * Nay lấy **TÂM HỘP BAO** cho `botX`: ổn định với MỌI ảnh (nghiêng hay không), vì hộp bao không
   * phụ thuộc hình dạng cổ. `botY` giữ nguyên (đáy dọc vẫn đúng — đó là cằm).
   */
  const boxCx = (minX + maxX) / 2;
  const [botX, botY] = toLocal(boxCx, botN ? botSy / botN : maxY);
  const color = cN
    ? ((Math.round(cr / cN) << 16) | (Math.round(cg / cN) << 8) | Math.round(cb / cN)) >>> 0
    : 0xf1c7a0;

  return { canvas: out, w: out.width, h: out.height, topX, topY, botX, botY, color };
}

/**
 * Bộ phận đọc được từ ảnh AI.
 * Hỗ trợ 2 kiểu rig:
 *   - 1 khúc: arm (cả cánh tay) + shin (cả chân)
 *   - 2 khúc: upperArm + foreArm, thigh + shin (nếu ảnh có)
 */
export interface AiPartSet {
  head: AiPart;
  torso: AiPart;
  foot: AiPart;
  hand?: AiPart;
  /** cả cánh tay trong 1 mảnh (khi ảnh vẽ tay liền) */
  arm?: AiPart;
  upperArm?: AiPart;
  foreArm?: AiPart;
  /** quần (che phần hông + đùi) — dùng làm mảnh hông tĩnh */
  pants?: AiPart;
  thigh?: AiPart;
  shin: AiPart;
  source: string;
}

/**
 * Đường dẫn ảnh AI cho từng class — thêm dòng ở đây khi có ảnh mới.
 *
 * ⚠️ Đuôi file do `artUrl()` quyết định (`tools/sync-art.mjs` nén sang `.webp`),
 * KHÔNG sửa `.png` ở đây thành `.webp` bằng tay — xem `src/art/art-url.ts`.
 */
export const AI_SHEETS: Partial<Record<ClassId, string>> = {
  strength: 'art/ai/strength-parts.png',
  agility: 'art/ai/agility-parts.png',
  street: 'art/ai/street-parts.png',
  army: 'art/ai/army-parts.png',
  mother: 'art/ai/mother-parts.png',
};

/**
 * Ảnh AI cho QUÁI — cùng bố cục lưới 3×3 như nhân vật (xem CELL_ORDER).
 * Quái dùng ít xương hơn nên chỉ cần đầu · thân · cánh tay · cẳng chân · ủng.
 */
export const AI_ENEMY_SHEETS: Record<string, string> = {
  thug: 'art/ai/enemy-thug.png',
  runner: 'art/ai/enemy-runner.png',
  brawler: 'art/ai/enemy-brawler.png',
  gunner: 'art/ai/enemy-gunner.png',
  spawner: 'art/ai/enemy-spawner.png',
  brat: 'art/ai/enemy-brat.png',
  eliteBrat: 'art/ai/enemy-eliteBrat.png',
  stageBoss: 'art/ai/enemy-boss.png',
  /* Arena: BOSS ELISE (60s) và BOSS CUỐI Quái Khủng — 02/10/2026, trước đây hai con này rơi về SVG. */
  elise: 'art/ai/enemy-elise.png',
  titan: 'art/ai/enemy-titan.png',
};

/**
 * HỆ SỐ PHÓNG TO **CHỈ ĐỂ VẼ** theo `kind` — mặc định 1 (bảng rỗng vẫn giữ kiểu `Record` cho tương lai).
 *
 * ⚠️⚠️ ĐÂY LÀ HÌNH ẢNH, KHÔNG PHẢI CHỈ SỐ: bán kính va chạm/ tầm đánh/ máu vẫn do `sim/` quyết định.
 *
 * ⚠️⚠️ 02/10/2026 — **XOÁ HỆ SỐ 1,35 CỦA TITAN**. Trước đây titan `radius 44` + hệ số vẽ 1,35 để đạt
 * "~3 lần quái thường". Người dùng nay chốt *"kích cỡ boss cuối to khổng lồ"* và bản sửa đã nâng
 * `radius` 44 → **88** trong `ARENA_UNIT_DEF`, mà tầng vẽ suy cỡ sprite TRỰC TIẾP từ `radius` ⇒ giữ
 * thêm 1,35 là nhân đôi hai lần (gần 7 lần quái thường, tràn khỏi ô va chạm và lệch hẳn thân với vỏ).
 * MỘT nguồn sự thật cho cỡ titan = `radius` (sim). Muốn to/ nhỏ nữa thì sửa `radius`, KHÔNG thêm hệ số vẽ.
 */
export const AI_ENEMY_DRAW_SCALE: Record<string, number> = {};

/**
 * ẢNH AI CHO **ĐƠN VỊ KHÔNG-PHẢI-CLASS** — hiện chỉ có NÔNG DÂN của Arena.
 *
 * ⚠️⚠️ VÌ SAO CẦN BẢNG RIÊNG, KHÔNG NHÉT NÔNG DÂN VÀO `AI_SHEETS`:
 * `AI_SHEETS` có khoá `ClassId` và `unitview.ts` tra nó bằng `CLASSES[u.kind]`. `'farmer'` KHÔNG phải
 * class (`CLASSES` không có nó — nó là `ARENA_FARMER` của `sim/arena/world.ts`), nên nhét vào
 * `AI_SHEETS` là nói dối kiểu: mọi vòng lặp `Object.keys(AI_SHEETS) as ClassId[]` sẽ coi nông dân là
 * một class (giá tướng, danh sách mở khoá, nút HUD…).
 *
 * ⚠️ HỆ QUẢ CỦA VIỆC NÔNG DÂN KHÔNG CÓ ART (BUG CÓ SẴN ĐÃ ĐO ĐƯỢC): `unitview.buildView` rơi xuống
 * nhánh QUÁI ⇒ `ENEMIES['farmer'] ?? STAGE_BOSS` ⇒ **nông dân bị vẽ bằng rig quái màu trùm**. Người
 * chơi thấy một con quái nhỏ đi đào mỏ thay vì một bác nông dân.
 *
 * ⚠️ KHOÁ Ở ĐÂY PHẢI KHỚP `ARENA_FARMER` trong `sim/arena/world.ts`. Tầng art KHÔNG được import tầng
 * `sim/` (lớp vẽ dùng chung không được phụ thuộc một chế độ — xem chú thích `UnitViewsWorld` trong
 * `unitview.ts`), nên sự khớp này được KHOÁ BẰNG TEST đọc cả hai file dạng chuỗi:
 * `tests/arena-farmer-art.test.ts`.
 */
export const AI_UNIT_SHEETS: Record<string, string> = {
  farmer: 'art/ai/farmer-parts.png',
};

const COLS = 3;
const ROWS = 3;
/** Cỡ ô đem đi xử lý pixel (xem lý do trong `sliceSheet`). */
const CELL_MAX = 232;
/** ô nào là bộ phận nào, theo thứ tự đọc của prompt */
/**
 * Bố cục ô của ảnh 3×3 (đọc trái→phải, trên→dưới):
 *   [1] đầu   [2] thân        [3] tay trên (BỎ)   [4] CẢ CÁNH TAY + nắm đấm
 *   [5] quần   [6] cẳng chân  [7] ủng             [8] bàn tay XOÈ (BỎ)   [9] trống
 *
 * ⚠️⚠️ NGƯỜI DÙNG CHỐT (đã đo trên cả 5 sheet, KHÔNG đoán):
 * *"bỏ bàn tay, thay cánh tay bằng cả cánh tay ở ảnh 3x3 dòng 2 cột 1"*
 *
 *   · Ô 4 (r2c1) = **CẢ CÁNH TAY**, vẽ từ vai xuống tận NẮM ĐẤM — đây mới là mảnh tay đúng.
 *     Đo bbox: cao ~322px trong khi thân cao ~292px ⇒ tay/thân = **1,10** (cả 5 sheet đều vậy).
 *   · Ô 3 (r1c3) = bản **CẮT CỤT** của chính cánh tay đó (dừng ở giữa cẳng tay, không có bàn tay).
 *     Trước đây code ưu tiên ô 3 ⇒ tay ngắn cụt rồi phải dán thêm ô 8 vào cuối.
 *   · Ô 8 (r3c2) = **bàn tay XOÈ 5 ngón** (nhiều sheet còn dính vệt đen ở mép ô) ⇒ BỎ HẲN:
 *     bàn tay đã nằm sẵn trong nắm đấm của ô 4, dán thêm là thành HAI bàn tay.
 *
 * ⇒ Nay: ô 4 gán vào `arm` (rig dùng thẳng mảnh này); ô 3 + ô 8 để `empty` (không nạp, không dùng).
 *    Bỏ ô 8 cũng chính là cách gỡ bàn tay: `ai.hand` không còn ⇒ nhánh dán bàn tay trong rig
 *    (`handPart0 = ai.hand ?? ai.hand_L`) tự động không chạy, không cần sửa rig.
 */
const CELL_ORDER: Array<keyof Omit<AiPartSet, 'source'> | 'empty'> = [
  'head', 'torso', 'empty',
  'arm', 'pants', 'shin',
  'foot', 'empty', 'empty',
];

/**
 * BỐ CỤC Ô **RIÊNG** CHO TỪNG SHEET KHÔNG-PHẢI-CLASS — hiện chỉ nông dân.
 *
 * ⚠️⚠️ VÌ SAO NÔNG DÂN KHÔNG DÙNG ĐƯỢC `CELL_ORDER` (ĐỌC `art/ai/prompt-farmer.txt` TRƯỚC KHI SỬA):
 * `CELL_ORDER` là bố cục của 5 SHEET CLASS: ô 3 để TRỐNG và ô 4 là **CẢ CÁNH TAY + nắm đấm** (xem chú
 * thích dài ở `CELL_ORDER`: người dùng đã chốt "bỏ bàn tay, dùng cả cánh tay ở ô 4").
 *
 * `farmer-parts.png` do Lead generate theo prompt RIÊNG, và prompt đó chia tay thành 2 ô:
 *   ô 3 = **TAY TRÊN** (vai→khuỷu) · ô 4 = **CẲNG TAY + bàn tay** (khuỷu→cổ tay, tay buông).
 * Nếu vẫn dùng `CELL_ORDER` thì ô 4 (chỉ là CẲNG TAY) bị gán vào `arm` ⇒ nhân vật mất hẳn tay trên
 * (đúng lỗi "tay ngắn cụt" mà repo đã ghi cho sheet class cũ), còn ô 3 bị vứt đi.
 * ⇒ Nông dân dùng ĐÚNG 2 khúc mà rig đã hỗ trợ sẵn (`ai.upperArm` + `ai.foreArm`, xem
 * `buildAiRigFromParts`: `singleArm = !ai.upperArm || !ai.foreArm`).
 *
 * ⚠️ BỐN Ô CÒN LẠI GIỐNG `CELL_ORDER`: ô 1 đầu · ô 2 thân · ô 5 quần (mảnh hông TĨNH, cắt 55% + vát
 * chữ V) · ô 6 cẳng chân · ô 7 ủng. Ô 8 (bàn tay XOÈ) + ô 9 để TRỐNG — bàn tay đã nằm sẵn trong ô 4,
 * dán thêm là thành HAI bàn tay (bài học y hệt của sheet class).
 *
 * ⚠️ ĐÃ ĐỐI CHIẾU BẰNG MẮT TRÊN CHÍNH `art/ai/farmer-parts.png`: ô 5 là CẢ ĐÔI QUẦN (cạp + hai ống
 * tới dưới gối) nên phải là `pants` (mảnh hông tĩnh) — gán nó vào `thigh` sẽ vẽ cả đôi quần cho TỪNG
 * chân (chồng nhau). Vì sheet này không có ô "đùi riêng", chân đi 1 khúc từ hông như mọi sheet class.
 */
const UNIT_CELL_ORDER: Record<string, Array<keyof Omit<AiPartSet, 'source'> | 'empty'>> = {
  farmer: ['head', 'torso', 'upperArm', 'foreArm', 'pants', 'shin', 'foot', 'empty', 'empty'],
};

/**
 * Tách nền magenta: pixel càng gần #FF00FF thì càng trong suốt.
 *
 * ⚠️ Dùng BÌNH PHƯƠNG khoảng cách, KHÔNG dùng `Math.hypot`: hàm này bị gọi cho
 * ~100.000 pixel mỗi ô ảnh, mà `Math.hypot` chậm gấp nhiều lần phép nhân.
 * (Đây là một phần lý do boot từng mất 13 giây.)
 */
export function chromaKeyMagenta(data: Uint8ClampedArray): void {
  const CUT = 130 * 130;
  const EDGE = 210 * 210;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const dr = r - 255;
    const db = b - 255;
    const d2 = dr * dr + g * g + db * db;
    if (d2 < CUT) {
      data[i + 3] = 0;
    } else if (d2 < EDGE) {
      const d = Math.sqrt(d2);
      data[i + 3] = Math.min(data[i + 3], Math.round(((d - 130) / 80) * 255));
      // khử viền hồng còn sót
      const spill = Math.min(r, b) - g;
      if (spill > 0) {
        data[i] = Math.max(0, r - spill);
        data[i + 2] = Math.max(0, b - spill);
      }
    }
  }
}

/**
 * GIỮ LẠI KHỐI LIỀN LỚN NHẤT trong 1 ô.
 *
 * Vì sao cần: các mảnh trong ảnh khá to (cẳng tay cao 324/341 px) nên TRÀN sang ô
 * bên cạnh. Khi đó hộp bao của ô sẽ bao gồm cả mảnh hàng xóm ⇒ ủng bị kéo dài và
 * lệch. Bộ lọc này xoá mọi mảng "dính" từ ô khác, chỉ giữ bộ phận chính giữa ô.
 */
function keepMainBlob(data: Uint8ClampedArray, w: number, h: number): void {
  const label = new Int32Array(w * h).fill(-1);
  const comps: Array<{ area: number; minX: number; minY: number; maxX: number; maxY: number }> = [];
  const stack: number[] = [];
  const solid = (i: number): boolean => data[i * 4 + 3] > 16;

  for (let i = 0; i < w * h; i++) {
    if (label[i] !== -1 || !solid(i)) continue;
    const id = comps.length;
    let area = 0;
    let minX = w;
    let minY = h;
    let maxX = -1;
    let maxY = -1;
    stack.push(i);
    label[i] = id;
    while (stack.length) {
      const p = stack.pop() as number;
      const x = p % w;
      const y = (p - x) / w;
      area++;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (x > 0 && label[p - 1] === -1 && solid(p - 1)) (label[p - 1] = id), stack.push(p - 1);
      if (x < w - 1 && label[p + 1] === -1 && solid(p + 1)) (label[p + 1] = id), stack.push(p + 1);
      if (y > 0 && label[p - w] === -1 && solid(p - w)) (label[p - w] = id), stack.push(p - w);
      if (y < h - 1 && label[p + w] === -1 && solid(p + w)) (label[p + w] = id), stack.push(p + w);
    }
    comps.push({ area, minX, minY, maxX, maxY });
  }
  if (comps.length <= 1) return;

  let best = 0;
  let bestScore = -Infinity;
  for (let k = 0; k < comps.length; k++) {
    const c = comps[k];
    const ccx = (c.minX + c.maxX) / 2;
    const ccy = (c.minY + c.maxY) / 2;
    const offCentre = Math.hypot(ccx - w / 2, ccy - h / 2) / Math.max(w, h);
    const score = c.area * (1 - Math.min(0.92, offCentre * 1.8));
    if (score > bestScore) {
      bestScore = score;
      best = k;
    }
  }
  for (let i = 0; i < w * h; i++) {
    if (label[i] !== -1 && label[i] !== best) data[i * 4 + 3] = 0;
  }
}

/**
 * CẮT CHÉO HAI BÊN ĐÙI của mảnh HÔNG — đường cắt CONG MỀM, vươn tới tận THẮT LƯNG.
 *
 * Xoá (alpha = 0) mọi pixel nằm dưới đường biên `yBound(x)`, với đường biên là một CUNG CONG:
 *   · ở GIỮA  (`u = 0`): giữ tới ĐÁY mảnh — đây là chữ V của bẹn;
 *   · ở hai MÉP (`u = 1`): chỉ giữ tới `ySide` ≈ **thắt lưng** — hai bên đùi bị cắt sát tận lưng;
 *   · ở giữa hai mốc: `yBound = ySide + (yBot − ySide) · cos(u · π/2)` — cung cos nên mềm, không gãy
 *     khúc như đường thẳng (người dùng: *"cong mềm mại đến tận thắt lưng"*).
 *
 * ⚠️ LỊCH SỬ (đừng lùi về đường thẳng):
 *   · Bản 1 cắt NGANG 55%  ⇒ người dùng: *"vẫn có cả đùi"*.
 *   · Bản 2 cắt CHÉO THẲNG từ `yApex = 58%` ⇒ hết đùi nhưng mép cắt còn thô và chỉ cao tới 58%.
 *   · Bản 3 (nay): cung cos, mép cắt cao tận `ySide = 10%` (sát thắt lưng) và bo mềm.
 *
 * @param canvas mảnh hông ĐÃ qua `processCell` (nền đã tách, đã cắt sát viền)
 * @param sideFrac mức giữ ở hai MÉP theo tỉ lệ chiều cao mảnh (0,10 = sát thắt lưng)
 */
function trimHipDiagonals(canvas: HTMLCanvasElement, sideFrac = 0.1, leftExtra = 0.44): void {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const cx = w / 2;
  const halfW = w / 2;
  const ySide = Math.round(h * sideFrac);
  const yBot = h - 1;
  for (let x = 0; x < w; x++) {
    // u: 0 ở giữa → 1 ở mép; cung cos cho đường biên cong mềm
    const u = Math.abs(x + 0.5 - cx) / halfW;
    let yBound = ySide + (yBot - ySide) * Math.cos((u * Math.PI) / 2);
    /*
     * ⚠️ LẸM SÂU HƠN Ở BÊN TRÁI — người dùng: *"Cắt giúp tôi lẹm quần phía bên trái vòng cung vào
     * sâu hơn nữa"*.
     *
     * Vì sao cần RIÊNG bên trái: đường may bẹn trong ảnh AI không đối xứng (nhân vật vẽ hơi xoay),
     * nên cung cắt đối xứng của bản cũ ăn vào bên phải vừa đúng nhưng để lại một mẩu bên TRÁI.
     *
     * Cách làm: hạ đường biên `yBound` xuống thêm một lượng cho các cột bên trái, lớn nhất ở mép
     * trái và giảm dần về giữa (nhân theo `uLeft` = độ xa tâm về phía trái) ⇒ vẫn là VÒNG CUNG mềm,
     * không thành bậc thang.
     *
     */
    /*
     * ⚠️⚠️ DẤU: `yBound` là ĐƯỜNG BIÊN — code dưới đặt alpha = 0 cho mọi `y > yBound`, tức CẮT
     * phần dưới đường biên. Vậy `yBound` CÀNG NHỎ thì cắt CÀNG SÂU.
     *
     * LỖI TÔI ĐÃ MẮC: bản trước tôi CỘNG (`yBound += …`) ⇒ đường biên hạ xuống ⇒ giữ lại nhiều hơn
     * ⇒ cắt NÔNG hơn. Người dùng chỉ ra đúng: *"bạn đang làm ngược vòng cung nông hơn rồi"*.
     * Nay TRỪ để hạ đường biên ⇒ cắt sâu hơn, đúng yêu cầu.
     *
     * `leftExtra = 0.44` ⇒ sâu thêm tối đa 44% chiều cao mảnh ở sát mép trái, giảm dần về 0 ở giữa
   * (người dùng: *"Sâu thêm 1 tí xíu nữa"* — nâng từ 0,34 lên 0,44)
     * (nhân theo `uLeft` = độ xa tâm về phía trái) ⇒ vẫn là VÒNG CUNG mềm nối vào khe chữ V, không
     * thành bậc thang. Người dùng chốt: *"phần bên phải ảnh quần ok, phần bên trái cắt từ khe chữ V
     * vòng cung sâu hơn"* ⇒ chỉ đụng bên trái, giữ nguyên bên phải.
     */
    const uLeft = x + 0.5 < cx ? (cx - (x + 0.5)) / halfW : 0;
    if (uLeft > 0) yBound -= (yBot - ySide) * leftExtra * uLeft;
    for (let y = Math.floor(yBound) + 1; y < h; y++) d[(y * w + x) * 4 + 3] = 0;
  }
  ctx.putImageData(img, 0, 0);
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

const cache = new Map<string, AiPartSet | null>();

/** Cắt ảnh lưới 3×3 thành 9 mảnh rời, tách nền magenta + cắt sát viền. */
async function sliceSheet(
  factory: PartFactory,
  url: string,
  prefix: string,
  issues: string[] = [],
  /**
   * BỐ CỤC Ô CỦA RIÊNG SHEET NÀY — mặc định `CELL_ORDER` (sheet class/ quái/ ult/ dáng).
   * Chỉ nông dân truyền bố cục riêng; xem `UNIT_CELL_ORDER` để hiểu vì sao.
   */
  cellOrder: Array<keyof Omit<AiPartSet, 'source'> | 'empty'> = CELL_ORDER,
): Promise<AiPartSet | null> {
  const t0 = performance.now();
  const img = await loadImage(url);
  if (!img) {
    console.warn(`[art] không nạp được ảnh AI: ${url}`);
    issues.push('không nạp được ảnh (sai đường dẫn hoặc file hỏng)');
    return null;
  }
  /*
   * ⚠️ KHÔNG CẢNH BÁO "ảnh không chia hết cho 3" — bản đầu tôi thêm và nó SAI.
   *
   * Mọi sheet AI của repo là **1024×1024**, mà 1024 % 3 = 1 ⇒ cảnh báo hiện lên với CHÍNH ẢNH
   * CHUẨN, người dùng tưởng ảnh mình hỏng. Phép cắt dùng `floor` nên chỉ bỏ ≤ 2px ở mép phải/
   * dưới — vô hại. Cái thật sự nguy hiểm là ảnh KHÔNG VUÔNG (ô bị kéo giãn) — `check:skills` báo.
   */
  const cw = Math.floor(img.width / COLS);
  const ch = Math.floor(img.height / ROWS);
  /*
   * HẠ CỠ Ô NGAY KHI CẮT.
   *
   * Ô gốc 341×341 px, nhưng mảnh cuối cùng chỉ vào atlas ở cỡ ≤192px ⇒ xử lý pixel
   * ở 341px là LÀM THỪA hơn 2 lần (tách nền + lọc khối + quét đều theo số pixel).
   * Kéo ô về 232px cho ra mảnh ~190px — gần như y hệt bản cũ mà nhanh hơn hẳn.
   */
  const cellScale = Math.min(1, CELL_MAX / cw);
  const pw = Math.max(1, Math.round(cw * cellScale));
  /*
   * ⚠️⚠️ CÓ Ô VẼ TRÀN QUA MỐC Ô — ĐO ĐƯỢC, PHẢI CẮT THÊM XUỐNG DƯỚI.
   *
   * Người dùng: *"sao tôi không thấy phần thân dưới?"* (của CÁNH TAY).
   * ĐO trên cả 5 sheet, cột 1: mảnh TAY (hàng 2) không dừng ở mốc hàng `y = 682` mà chạy tiếp
   * xuống **`y = 711`** ⇒ **nắm đấm nằm dưới mốc 29px** và bị phép cắt-theo-ô CẮT MẤT, nên nhân
   * vật ra trận chỉ có cánh tay cụt tới cổ tay (không có bàn tay).
   *
   * Vì sao an toàn khi nới xuống: mảnh ỦNG ở ô dưới bắt đầu từ `y = 807` (cách 96px, một khối
   * RỜI) nên `keepMainBlob` — vốn chỉ giữ khối LIỀN lớn nhất — vẫn giữ đúng tay+nắm đấm và dọn
   * mọi thứ khác lọt vào dải nới. Nới 15% chiều cao ô (51px) là đủ chứa 29px tràn mà không chạm ủng.
   *
   * ⚠️ CHỈ nới cho ô `arm`. Các ô khác đã nằm gọn trong ô (đo rồi), nới bừa sẽ kéo nội dung ô
   * bên dưới vào và làm hỏng mảnh.
   */
  const CELL_OVERFLOW_DOWN: Partial<Record<string, number>> = { arm: 0.15 };
  /*
   * ⚠️⚠️ Ô HÔNG (ô 5 = hàng 2 cột 2) — CHỈ LẤY DẢI TRÊN, TỪ THẮT LƯNG TỚI CHỮ V BẸN.
   *
   * Người dùng: *"các nhân vật thêm phần hông (chỉ cắt từ thắt lưng đến và lấy phần chữ V bẹn)
   * … cắt từ ảnh 3x3 hàng 2 cột 2"*.
   *
   * Ô 5 vẽ CẢ QUẦN ĐÙI (hông + hai ống xuống tới gần gối). Nếu dán nguyên mảnh làm mảnh hông TĨNH
   * thì nó chồng lên hai ống chân đang cử động (lỗi đã ghi ở đầu `buildAiRigFromParts`). Người dùng
   * chốt: chỉ lấy phần HÔNG, dừng ở chữ V bẹn.
   *
   * ĐO trên 3 sheet (nội dung ô 5 cao 295–302px): đỉnh thắt lưng ở `y ≈ 39–46`, chữ V bẹn ở
   * `y ≈ 170–200` ⇒ giữ **55% chiều cao ô** (0..188px) là vừa ĐÚNG chữ V cho cả 4 sheet có quần,
   * KHÔNG còn dư một đoạn đùi phía dưới. (Với `mother` ô 5 là MỘT ĐÙI chứ không phải quần — vẫn
   * lấy dải trên, xem chú thích ở khối vẽ hông trong `character.ts`.)
   *
   * ⚠️ LỊCH SỬ: bản đầu để **60%** ⇒ người dùng: *"tôi thấy phần thắt lưng vẫn có cả đùi, chỉ cắt
   * lấy chữ V của bẹn thôi"*. Đã dựng ảnh so 45/50/55/60% cho cả 4 class: ở 45–50% thì chữ V CHƯA
   * thành hình (mép cắt nằm trên V), ở 60% thì thừa một đốt đùi; **55%** là mức V vừa trọn.
   */
  const CELL_KEEP_TOP: Partial<Record<string, number>> = { pants: 0.55 };
  const parts: Partial<Record<keyof Omit<AiPartSet, 'source'>, AiPart>> = {};

  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const name = cellOrder[r * COLS + c];
      if (name === 'empty') continue;
      /*
       * Chiều cao NGUỒN của ô: mặc định `ch`; ô khai báo `CELL_KEEP_TOP` thì CHỈ lấy phần trên,
       * ô khai báo `CELL_OVERFLOW_DOWN` thì lấy tràn xuống dưới; và LUÔN kẹp trong ảnh
       * (`img.height`) để `drawImage` không đọc ra ngoài.
       */
      const over = CELL_OVERFLOW_DOWN[name] ?? 0;
      const keep = CELL_KEEP_TOP[name] ?? 1;
      const srcH = Math.min(img.height - r * ch, Math.round(ch * keep) + Math.round(ch * over));
      const phSrc = Math.max(1, Math.round(srcH * cellScale));
      // cắt ô (đã hạ cỡ)
      const cell = document.createElement('canvas');
      cell.width = pw;
      cell.height = phSrc;
      const cctx = cell.getContext('2d', { willReadFrequently: true })!;
      cctx.imageSmoothingEnabled = true;
      cctx.imageSmoothingQuality = 'high';
      cctx.drawImage(img, c * cw, r * ch, cw, srcH, 0, 0, pw, phSrc);

      // tách nền + lọc khối + cắt sát viền + thu nhỏ: MỘT lượt (xem processCell)
      const proc = processCell(cell);
      if (!proc) {
        console.warn(`[art] ô "${name}" trống trong ${url}`);
        issues.push(`ô ${name} trống`);
        continue;
      }
      /*
       * ⚠️⚠️ Ô HÔNG: CẮT CHÉO HAI BÊN ĐÙI — theo ĐÚNG đường may chữ V của bẹn.
       *
       * Người dùng: *"tôi thấy phần thắt lưng vẫn có cả đùi, chỉ cắt lấy chữ V của bẹn thôi"* →
       * *"ý tôi là cắt chéo 2 bên đùi đi ý"*.
       *
       * Ảnh gốc: ở nửa dưới mảnh hông, đường may bẹn là một chữ **V** — thấp nhất ở GIỮA, cao dần ra
       * hai bên (đã phóng 4× kiểm: vệt may chạy từ hông trái xuống đáy giữa rồi lên hông phải).
       * Dưới chữ V đó là HAI ĐÙI. Cắt bỏ đúng hai phần dưới chữ V, giữ lại phần trên.
       *
       * Công thức: giữ pixel khi `y ≤ yLine(x)` với `yLine` thấp ở giữa và cao ở hai mép
       *   `yLine(x) = yApex + (yBot - yApex) · (1 - |x - cx| / halfW)`
       * (đảo lại thành bề rộng được giữ theo từng hàng: `keepHalf(y) = halfW · (1 - t)`, `t` chạy
       * 0→1 từ đỉnh V xuống đáy). `yApex = 58%` chiều cao mảnh — đo trên 3 sheet có quần, mép chữ V
       * ở hai bên hông nằm quanh 54–58%.
       */
      if (name === 'pants') trimHipDiagonals(proc.canvas);
      const key = `${prefix}_${name}`;
      const added = factory.addCanvasFrame(key, proc.canvas);
      if (!added) {
        issues.push(`atlas đầy — không nạp được ô ${name}`);
        continue;
      }
      parts[name] = { key, w: added.w, h: added.h, topX: proc.topX, topY: proc.topY, botX: proc.botX, botY: proc.botY, color: proc.color };
      console.log(
        `[art]   ${name}: ${added.w}×${added.h} · gốc trên (${proc.topX.toFixed(0)},${proc.topY.toFixed(0)}) · đáy (${proc.botX.toFixed(0)},${proc.botY.toFixed(0)}) · màu #${proc.color.toString(16).padStart(6, '0')}`,
      );
    }
  }

  const required: Array<keyof Omit<AiPartSet, 'source'>> = ['head', 'torso', 'foot', 'shin'];
  const missing = required.filter((k) => !parts[k]);
  // cần ít nhất 1 cách dựng tay
  if (!parts.arm && !(parts.upperArm && parts.foreArm)) missing.push('arm');
  if (missing.length) {
    console.warn(`[art] ảnh AI ${url} thiếu bộ phận: ${missing.join(', ')}`);
    issues.push(`thiếu ô bắt buộc: ${missing.join(', ')}`);
    return null;
  }

  const set: AiPartSet = {
    head: parts.head!,
    torso: parts.torso!,
    foot: parts.foot!,
    shin: parts.shin!,
    ...(parts.arm ? { arm: parts.arm } : {}),
    ...(parts.upperArm ? { upperArm: parts.upperArm } : {}),
    ...(parts.foreArm ? { foreArm: parts.foreArm } : {}),
    ...(parts.hand ? { hand: parts.hand } : {}),
    ...(parts.pants ? { pants: parts.pants } : {}),
    ...(parts.thigh ? { thigh: parts.thigh } : {}),
    source: url,
  };
  const t1 = performance.now();
  console.log(
    `[art] đã nạp ${url} trong ${Math.round(t1 - t0)}ms: đầu ${set.head.w}×${set.head.h} · thân ${set.torso.w}×${set.torso.h}` +
      ` · tay ${set.arm ? 'cả cánh tay ' + set.arm.h : `${set.upperArm?.h ?? '?'}+${set.foreArm?.h ?? '?'}`}` +
      ` · chân ${set.pants ? 'quần ' + set.pants.h : `đùi ${set.thigh?.h ?? '?'}`}+${set.shin.h}`,
  );
  return set;
}

/**
 * Nạp ảnh AI cho 1 class. Trả về null nếu chưa có ảnh ⇒ game tự lùi về art vector.
 * Chỉ nạp 1 lần cho mỗi class.
 */
export async function loadAiParts(factory: PartFactory, classId: ClassId): Promise<AiPartSet | null> {
  if (cache.has(classId)) return cache.get(classId) ?? null;
  const url = AI_SHEETS[classId];
  if (!url) {
    cache.set(classId, null);
    return null;
  }
  const set = await sliceSheet(factory, artUrl(url), `ai_${classId}`);
  cache.set(classId, set);
  return set;
}

/** Nạp ảnh AI cho 1 loại QUÁI. null ⇒ dùng rig vector. */
/**
 * ART RIÊNG CHO ULTIMATE — nạp 1 lần cho mỗi khoá.
 *
 * Người dùng: *"Sao ví dụ như Strength dùng ult biến hình mà không đổi hình ảnh nhân vật thành Dã
 * trư khổng lồ?"* ⇒ ultimate biến hình PHẢI đổi cả HÌNH, không chỉ chỉ số.
 *
 * Vì sao dùng CHUNG `sliceSheet` với class/ quái: lưới 3×3 bộ phận + tách nền magenta + trim đã
 * được viết và kiểm kỹ ở đó; viết đường nạp thứ hai sẽ lệch chuẩn hoá tỉ lệ.
 * Trả `null` nếu chưa có ảnh (art đang được vẽ) ⇒ nơi dựng rig tự lùi về hình class gốc.
 */
export async function loadUltParts(factory: PartFactory, artId: string): Promise<AiPartSet | null> {
  const cacheKey = `ult:${artId}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey) ?? null;
  /*
   * ⚠️ PHẢI đi qua `artPartsUrl` — không hardcode `.png`.
   * Art ultimate (`boar`/`maybay`/`xebom`) cũng là ảnh AI nên `sync-art.mjs` nén sang
   * `.webp`. Hardcode `.png` ở đây từng làm ảnh Dã trư **404 và im lặng** — đo được
   * bằng Chrome thật: vẫn có request `art/ai/boar-parts.png` dù `.webp` đã tồn tại.
   */
  const url = artPartsUrl(artId);
  const set = await sliceSheet(factory, url, `ult_${artId}`);
  cache.set(cacheKey, set);
  return set;
}

export async function loadAiEnemyParts(factory: PartFactory, enemyId: string): Promise<AiPartSet | null> {
  const cacheKey = `enemy:${enemyId}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey) ?? null;
  const url = AI_ENEMY_SHEETS[enemyId];
  if (!url) {
    cache.set(cacheKey, null);
    return null;
  }
  const set = await sliceSheet(factory, artUrl(url), `aienemy_${enemyId}`);
  cache.set(cacheKey, set);
  return set;
}

/**
 * Nạp ảnh AI cho 1 ĐƠN VỊ KHÔNG-PHẢI-CLASS (nông dân Arena). `null` ⇒ giữ nguyên hành vi cũ.
 *
 * ⚠️ ĐI QUA CHÍNH `sliceSheet`: cùng tách nền magenta, cùng lọc khối, cùng dò điểm khớp, cùng thu nhỏ
 * atlas. Viết đường nạp thứ hai là mở đường cho nông dân lệch chuẩn hoá tỉ lệ so với mọi nhân vật khác.
 * ⚠️ Bố cục ô lấy từ `UNIT_CELL_ORDER` (khác `CELL_ORDER`) — xem chú thích ở đó.
 */
export async function loadAiUnitParts(factory: PartFactory, unitId: string): Promise<AiPartSet | null> {
  const cacheKey = `unit:${unitId}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey) ?? null;
  const url = AI_UNIT_SHEETS[unitId];
  if (!url) {
    cache.set(cacheKey, null);
    return null;
  }
  const set = await sliceSheet(factory, artUrl(url), `aiunit_${unitId}`, [], UNIT_CELL_ORDER[unitId] ?? CELL_ORDER);
  cache.set(cacheKey, set);
  return set;
}

/** Kiểm tra nhanh ảnh có nạp được không (dùng cho công cụ kiểm chứng). */
export async function probeAiParts(url: string): Promise<{ ok: boolean; w?: number; h?: number }> {
  const img = await loadImage(url);
  return img ? { ok: true, w: img.width, h: img.height } : { ok: false };
}

/**
 * ẢNH "DÁNG" (skill form) — MỘT LƯỚI 3×3 CHO MỘT FILE TRONG `art/skills/<class>/`.
 *
 * Dùng CHUNG đường cắt với ảnh class (`sliceSheet`): cùng bố cục ô, cùng tách nền magenta,
 * cùng dò điểm khớp ⇒ dáng do người dùng thả vào tự động ăn khớp hệ xương, không phải căn tay.
 *
 * ⚠️ NẠP THEO NHU CẦU, KHÔNG NẠP HẾT LÚC BOOT: người dùng có thể thả hàng chục ảnh 3×3; mỗi
 * ảnh chiếm ~6 mảnh trong atlas 3072². Chỉ nạp ảnh đang chọn/ đang xem trước (và nạp trước các
 * dáng đang mặc khi vào trận) ⇒ atlas không bị đầy vì ảnh chưa ai xem.
 */
export interface AiSkillSheetResult {
  /** null nếu ảnh hỏng/ thiếu ô/ atlas đầy — nhân vật lùi về dáng gốc của class */
  set: AiPartSet | null;
  /** câu mô tả lỗi để UI hiển thị cho người dùng biết sửa ảnh nào */
  issues: string[];
}

const skillSheetCache = new Map<string, AiSkillSheetResult>();

/**
 * Nạp 1 dáng. Kết quả (kể cả THẤT BẠI) được cache ⇒ bấm qua bấm lại không cắt lại ảnh.
 * `classId`/`formId` chỉ dùng làm khoá cache + tiền tố frame (không ảnh hưởng cách dựng xương).
 */
export async function loadAiSkillForm(
  factory: PartFactory,
  classId: string,
  formId: string,
  url: string,
): Promise<AiSkillSheetResult> {
  const cacheKey = `${classId}:${formId}`;
  const hit = skillSheetCache.get(cacheKey);
  if (hit) return hit;
  const issues: string[] = [];
  /*
   * GOM LÔ khi cắt ảnh: mỗi mảnh vào atlas là 1 lần `atlas.refresh()` (đẩy ~37 MB lên GPU) ⇒
   * nạp 1 dáng mà không gom là 6 lần upload. `beginBatch`/`endBatch` đếm độ sâu nên nạp xen
   * giữa `preloadAll()` cũng không phá lô đang chạy.
   */
  factory.beginBatch();
  let set: AiPartSet | null = null;
  try {
    set = await sliceSheet(factory, url, `aiskill_${classId}_${formId}`, issues);
  } finally {
    factory.endBatch();
  }
  const result: AiSkillSheetResult = { set, issues };
  skillSheetCache.set(cacheKey, result);
  return result;
}

/** Xoá cache dáng (dùng cho công cụ kiểm chứng khi thay ảnh giữa 2 lần đo). */
export function clearSkillFormCache(): void {
  skillSheetCache.clear();
}

export { PARTS_ATLAS };
